import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import Button from '../components/Button';
import Input from '../components/Input';
import {
  Shield, 
  Zap, 
  Clock, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  LogIn, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  Users,
  BarChart3,
  Smartphone
} from 'lucide-react';
import { getApiBaseUrl } from '../config/apiBaseUrl';
const GOOGLE_OAUTH_UI_ENABLED = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED !== 'false';

const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();
  
  // États du formulaire
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      showError('Champs requis', 'Veuillez remplir tous les champs');
      setIsSubmitting(false);
      return;
    }

    try {
      await login(email, password);
      showSuccess('Connexion réussie', 'Bienvenue dans InventWee !');
      // Forcer un rechargement pour que App.tsx détecte la connexion
      setTimeout(() => window.location.href = '/', 1000);
    } catch (err: any) {
      console.error('Erreur de connexion:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erreur de connexion';
      setError(errorMessage);
      showError('Erreur de connexion', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Raccourcis pour les identifiants de test
  const fillAdminCredentials = () => {
    setEmail('admin@inventwee.com');
    setPassword('admin123');
  };

  const fillUserCredentials = () => {
    setEmail('user@inventwee.com');
    setPassword('user123');
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oe = params.get('oauth_error');
    if (oe) {
      showError('Connexion Google', oe);
      window.history.replaceState({}, '', '/login');
    }
  }, [showError]);

  const startGoogleLogin = () => {
    window.location.href = `${getApiBaseUrl()}/auth/google`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo & Branding */}
          <div className="flex items-center space-x-4 mb-16">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl overflow-hidden p-1">
              <img src="/logo-carton.svg" alt="" className="w-11 h-11 object-contain drop-shadow-md" width={44} height={44} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white mb-1">InventWee</h1>
              <p className="text-indigo-100 text-lg font-medium">Système d'Inventaire</p>
              <p className="text-indigo-200 text-sm">Développé par AirZk.fr</p>
            </div>
          </div>

          {/* Features avec animations */}
          <div className="space-y-10">
            <div className="flex items-start space-x-5 group">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-2">Scan Ultra-Rapide</h3>
                <p className="text-indigo-100 text-base leading-relaxed">
                  Scannez vos objets pédagogiques en quelques secondes avec notre technologie QR avancée et reconnaissance d'objets IA
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-5 group">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-2">Synchronisation Temps Réel</h3>
                <p className="text-indigo-100 text-base leading-relaxed">
                  Vos données sont synchronisées instantanément sur tous vos appareils avec une architecture offline-first
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-5 group">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-2">Analytics Avancés</h3>
                <p className="text-indigo-100 text-base leading-relaxed">
                  Suivez l'utilisation, les mouvements et générez des rapports détaillés de votre inventaire
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <div className="flex items-center space-x-6 text-indigo-200 text-sm">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Multi-utilisateurs</span>
            </div>
            <div className="flex items-center space-x-2">
              <Smartphone className="w-4 h-4" />
              <span>Mobile-First</span>
            </div>
          </div>
          <p className="text-indigo-200 text-sm mt-4">
            © 2025 InventWee. Tous droits réservés.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl shadow-2xl mb-6 p-2">
              <img src="/logo-carton.svg" alt="" className="w-14 h-14 object-contain drop-shadow" width={56} height={56} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">InventWee</h1>
            <p className="text-gray-600 font-medium">Système d'Inventaire</p>
            <p className="text-gray-500 text-sm">Développé par AirZk.fr</p>
          </div>

          {/* Welcome Section */}
          <div className="text-center lg:text-left mb-10">
            <div className="flex items-center justify-center lg:justify-start space-x-2 mb-4">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <h2 className="text-4xl font-bold text-gray-900">
                Bon retour !
              </h2>
            </div>
            <p className="text-gray-600 text-lg">
              Connectez-vous pour accéder à votre inventaire
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
            {GOOGLE_OAUTH_UI_ENABLED && (
              <>
                <button
                  type="button"
                  onClick={startGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-gray-200 bg-white text-gray-800 font-medium shadow-sm hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Se connecter avec Google
                </button>
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white/90 text-gray-500 font-medium">ou</span>
                  </div>
                </div>
              </>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <Input
                label="Adresse email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                icon={Mail}
                required
              />

              {/* Password Field */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50/50"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Se souvenir de moi
                  </label>
                </div>
                <a href="#" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  Mot de passe oublié ?
                </a>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-red-700 text-sm font-medium">{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isSubmitting}
                icon={!isSubmitting ? <LogIn className="w-5 h-5" /> : undefined}
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Connexion...' : 'Se connecter'}
                {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>
            </form>

            {/* Raccourcis démo : uniquement en dev (jamais dans le bundle prod servi aux utilisateurs) */}
            {import.meta.env.DEV && import.meta.env.VITE_HIDE_LOGIN_TEST_SHORTCUTS !== 'true' && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center mb-4">Identifiants de test :</p>
                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={fillAdminCredentials}
                    icon={<Shield className="w-4 h-4" />}
                    className="flex-1"
                  >
                    Admin
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={fillUserCredentials}
                    icon={<Users className="w-4 h-4" />}
                    className="flex-1"
                  >
                    Utilisateur
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Besoin d'aide ?{' '}
              <a href="#" className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors duration-200">
                Contactez le support
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={removeToast}
          />
        ))}
      </div>
    </div>
  );
};

export default LoginPage;